import styled, { css, keyframes } from 'styled-components';
import QR from '../atoms/QR/QR';
import { CHAT } from './tokens';
import type { ChatAttachment, ChatMessage, ChatState } from '../../services/conciergeChatTypes';
import { isVideoName } from '../../services/conciergeVideo';

// ─── Page frame: one column, 640px on a wide screen; header and composer stay in place ───────────
export const Frame = styled.div`
  height: 100dvh;
  max-width: 640px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background: #fff;
  color: ${CHAT.ink};
  font-family: ${CHAT.sans};
  @media (min-width: 768px) {
    border-left: 1px solid #f0f0f0;
    border-right: 1px solid #f0f0f0;
  }
`;

const HeaderBox = styled.header`
  flex: none;
  padding: 16px 16px 10px;
  border-bottom: 1px solid ${CHAT.line};
`;
const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${CHAT.title};
`;
const Sum = styled.span`
  font-family: ${CHAT.mono};
  font-size: 18px;
  font-weight: 700;
`;
const Sub = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: ${CHAT.muted};
  display: flex;
  align-items: center;
  gap: 6px;
`;
const Dot = styled.span<{ $on: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $on }) => ($on ? CHAT.action : '#d9b44a')};
`;

export const ChatHeader = ({ title, amount, status, online }: { title: string; amount: string | null; status: string; online: boolean | null }) => (
  <HeaderBox>
    <HeaderTop>
      <Title>{title}</Title>
      {amount && <Sum data-testid='chat-header-amount'>{amount}</Sum>}
    </HeaderTop>
    <Sub>
      {online !== null && <Dot $on={online} />}
      {status}
    </Sub>
  </HeaderBox>
);

// ─── Plaque above the feed: the outcome of the deposit ───────────────────────────────────────────
const PlaqueBox = styled.div<{ $tone: 'ok' | 'late' | 'neutral' }>`
  flex: none;
  margin: 12px 12px 0;
  padding: 12px 14px;
  border-radius: 16px;
  font-size: 14px;
  background: ${({ $tone }) => ($tone === 'ok' ? CHAT.surface : $tone === 'late' ? CHAT.yellow : CHAT.grey)};
  b {
    display: block;
    font-size: 15px;
    margin-bottom: 2px;
    color: ${({ $tone }) => ($tone === 'ok' ? CHAT.actionText : CHAT.ink)};
  }
`;

export const Plaque = ({ tone, title, text }: { tone: 'ok' | 'late' | 'neutral'; title: string; text: string | null }) => (
  <PlaqueBox $tone={tone} data-testid='chat-plaque'>
    <b>{title}</b>
    {text}
  </PlaqueBox>
);

// ─── Feed rows ───────────────────────────────────────────────────────────────────────────────────
export const FeedBox = styled.main`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
export const FeedFill = styled.div`
  flex: 1;
`;

const Who = styled.div`
  font-size: 11px;
  color: ${CHAT.muted};
  margin: 4px 4px -4px;
`;
const appear = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`;
const Bubble = styled.div<{ $mine: boolean; $appear?: boolean; $faded?: boolean }>`
  max-width: 82%;
  ${({ $appear }) =>
    $appear &&
    css`
      animation: ${appear} 0.18s ease-out both;
    `}
  opacity: ${({ $faded }) => ($faded ? 0.75 : 1)};
  transition: opacity 0.15s;
  padding: 9px 12px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  align-self: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
  background: ${({ $mine }) => ($mine ? CHAT.actionSoft : CHAT.grey)};
  ${({ $mine }) => ($mine ? 'border-bottom-right-radius: 4px;' : 'border-bottom-left-radius: 4px;')}
  a {
    color: ${CHAT.actionText};
  }
`;

const Thumb = styled.img`
  display: block;
  max-width: 200px;
  max-height: 200px;
  border-radius: 10px;
  object-fit: cover;
  background: #fff;
`;
const Clip = styled.video`
  display: block;
  max-width: 240px;
  max-height: 320px;
  border-radius: 10px;
  background: #000;
`;
const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  word-break: break-all;
`;
const FileIcon = () => (
  <svg width='26' height='30' viewBox='0 0 26 30' aria-hidden='true' style={{ flex: 'none' }}>
    <path d='M3 1h13l7 7v19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2z' fill='#fff' stroke={CHAT.action} strokeWidth='1.5' />
    <path d='M16 1v7h7' fill='none' stroke={CHAT.action} strokeWidth='1.5' />
  </svg>
);

const Attachments = ({ items, label }: { items: ChatAttachment[]; label: string }) => (
  <>
    {items.map((a) =>
      // A video plays in place; the link under it opens the file when the browser cannot play it.
      isVideoName(a.fileName || a.url) ? (
        <div key={a.url}>
          <Clip src={a.url} controls playsInline preload='metadata' data-testid='chat-video' />
          <a href={a.url} target='_blank' rel='noopener noreferrer'>
            <FileCard>
              <FileIcon />
              {a.fileName || label}
            </FileCard>
          </a>
        </div>
      ) : (
        <a key={a.url} href={a.url} target='_blank' rel='noopener noreferrer'>
          {a.image ? (
            <Thumb src={a.url} alt={a.fileName || label} loading='lazy' />
          ) : (
            <FileCard>
              <FileIcon />
              {a.fileName || label}
            </FileCard>
          )}
        </a>
      ),
    )}
  </>
);

/** Our own caption of a sent file («Файл: <name>») only repeats what the attachment shows. */
const captionOnly = (message: ChatMessage): boolean =>
  message.attachments.some((a) => message.text.trim() === `Файл: ${a.fileName}`);

export const MessageBubble = ({ message, author, fileLabel, showAuthor }: { message: ChatMessage; author: string; fileLabel: string; showAuthor: boolean }) => (
  <>
    {showAuthor && <Who>{author}</Who>}
    {/* The client's own message replaces its pending bubble, which already appeared. */}
    <Bubble $mine={message.from === 'client'} $appear={message.from !== 'client'} data-testid={`chat-message-${message.from}`}>
      {!captionOnly(message) && message.text}
      <Attachments items={message.attachments} label={fileLabel} />
    </Bubble>
  </>
);

const Delivery = styled.div<{ $failed: boolean }>`
  align-self: flex-end;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: -4px 4px 0;
  font-size: 11px;
  color: ${({ $failed }) => ($failed ? CHAT.warn : CHAT.muted)};
  button {
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    color: ${CHAT.actionText};
    text-decoration: underline;
    cursor: pointer;
  }
`;
const Clock = () => (
  <svg width='11' height='11' viewBox='0 0 24 24' aria-hidden='true'>
    <circle cx='12' cy='12' r='9' fill='none' stroke='currentColor' strokeWidth='2' />
    <path d='M12 7v5l3 2' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
  </svg>
);

/** A message the client sent that the server has not confirmed yet, or refused. */
export const PendingBubble = ({ text, fileName, preview, video, failed, sendingLabel, failedLabel, retryLabel, onRetry }: {
  text: string;
  /** A file being uploaded: its picked name, and a thumbnail when it is an image. */
  fileName: string | null;
  preview: string | null;
  /** A video: its preview is a muted clip. */
  video: boolean;
  failed: boolean;
  sendingLabel: string;
  failedLabel: string;
  retryLabel: string;
  onRetry: () => void;
}) => (
  <>
    <Bubble $mine $appear $faded={!failed} data-testid={failed ? 'chat-message-failed' : 'chat-message-sending'}>
      {fileName === null && text}
      {fileName !== null &&
        (preview && video ? (
          <Clip src={preview} muted playsInline preload='metadata' />
        ) : preview ? (
          <Thumb src={preview} alt={fileName} />
        ) : (
          <FileCard>
            <FileIcon />
            {fileName}
          </FileCard>
        ))}
    </Bubble>
    <Delivery $failed={failed}>
      {failed ? (
        <>
          {failedLabel}
          <button type='button' onClick={onRetry}>
            {retryLabel}
          </button>
        </>
      ) : (
        <>
          <Clock />
          {sendingLabel}
        </>
      )}
    </Delivery>
  </>
);

const spin = keyframes`to { transform: rotate(360deg); }`;
export const Spinner = styled.span`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border: 2px solid ${CHAT.actionSoft};
  border-top-color: ${CHAT.action};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;
const SystemRow = styled.div<{ $ok?: boolean; $left?: boolean }>`
  align-self: ${({ $left }) => ($left ? 'flex-start' : 'center')};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 13px;
  color: ${({ $ok }) => ($ok ? CHAT.actionText : CHAT.muted)};
  font-weight: ${({ $ok }) => ($ok ? 600 : 400)};
  background: ${({ $ok }) => ($ok ? CHAT.surface : '#fafafa')};
`;
const Check = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${CHAT.action};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const EventRow = ({ kind, text }: { kind: 'receipt' | 'completed' | 'expired'; text: string }) => (
  <SystemRow $ok={kind === 'completed'} data-testid={`chat-event-${kind}`}>
    {kind === 'completed' && (
      <Check>
        <svg width='10' height='8' viewBox='0 0 18 13' aria-hidden='true'>
          <path d='M2 6.5L6.5 11L15.6 2' stroke='#fff' strokeWidth='3' fill='none' strokeLinecap='round' />
        </svg>
      </Check>
    )}
    {text}
  </SystemRow>
);

export const StateRow = ({ text }: { state: ChatState; text: string }) => (
  <SystemRow $left data-testid='chat-state'>
    <Spinner />
    {text}
  </SystemRow>
);

export const NoticeRow = ({ text }: { text: string }) => <SystemRow>{text}</SystemRow>;

const QrBox = styled.div`
  align-self: flex-start;
  display: flex;
  gap: 12px;
  align-items: center;
  max-width: 420px;
  padding: 10px 14px;
  border: 1px dashed rgba(106, 170, 58, 0.6);
  border-radius: 14px;
  background: #fff;
  b {
    display: block;
    font-size: 14px;
    margin-bottom: 2px;
  }
  span {
    font-size: 12px;
    color: ${CHAT.muted};
  }
`;

export const QrReceiptRow = ({ url, title, text }: { url: string; title: string; text: string }) => (
  <QrBox data-testid='chat-qr'>
    <QR data={url} size={84} bgColor='#ffffff' fgColor='#111111' frameColor='#ffffff' />
    <div>
      <b>{title}</b>
      <span>{text}</span>
    </div>
  </QrBox>
);

// ─── Bottom: return button ───────────────────────────────────────────────────────────────────────
export const BackButton = styled.button<{ $primary: boolean }>`
  flex: none;
  margin: 0 12px 14px;
  padding: ${({ $primary }) => ($primary ? '12px' : '10px')};
  border: 0;
  border-radius: 12px;
  background: ${({ $primary }) => ($primary ? CHAT.paid : 'transparent')};
  color: ${({ $primary }) => ($primary ? '#fff' : CHAT.muted)};
  font-family: ${CHAT.sans};
  font-size: ${({ $primary }) => ($primary ? '15px' : '14px')};
  font-weight: ${({ $primary }) => ($primary ? 600 : 400)};
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
  }
`;
